package com.wiz3r.database.shared

import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import com.wiz3r.database.SchemaQueries
import com.wiz3r.database.Wiz3rDatabase
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass

internal val KClass<Wiz3rDatabase>.schema: SqlSchema<QueryResult.AsyncValue<Unit>>
  get() = Wiz3rDatabaseImpl.Schema

internal fun KClass<Wiz3rDatabase>.newInstance(driver: SqlDriver): Wiz3rDatabase =
    Wiz3rDatabaseImpl(driver)

private class Wiz3rDatabaseImpl(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver), Wiz3rDatabase {
  override val schemaQueries: SchemaQueries = SchemaQueries(driver)

  public object Schema : SqlSchema<QueryResult.AsyncValue<Unit>> {
    override val version: Long
      get() = 1

    override fun create(driver: SqlDriver): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS users (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT UNIQUE NOT NULL,
          |    username TEXT UNIQUE NOT NULL,
          |    password_hash TEXT NOT NULL DEFAULT '',
          |    email TEXT UNIQUE,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_login_at TEXT,
          |    -- MFA fields (only active in production mode)
          |    mfa_secret TEXT,
          |    mfa_enabled INTEGER NOT NULL DEFAULT 0,
          |    backup_codes TEXT,  -- JSON array of hashed backup codes
          |    -- Email verification
          |    email_verified INTEGER NOT NULL DEFAULT 0,
          |    verification_token TEXT,
          |    verification_token_expires_at TEXT,
          |    -- Password reset (server stores SHA-256 hash of the emailed token, not the
          |    -- token itself, so a DB read cannot impersonate an in-flight reset).
          |    password_reset_token_hash TEXT,
          |    password_reset_expires_at TEXT,
          |    -- Stripe subscription fields
          |    stripe_customer_id TEXT,
          |    subscription_status TEXT NOT NULL DEFAULT 'trialing',
          |    subscription_id TEXT,
          |    trial_ends_at TEXT NOT NULL DEFAULT (datetime('now', '+14 days')),
          |    subscription_tier TEXT NOT NULL DEFAULT 'standard',
          |    -- Notification support
          |    last_activity_at TEXT,
          |    phone_number TEXT,
          |    -- Wiz 3R AI feature flag (DB-backed, server-enforced).
          |    -- 0 = AI off (default), 1 = AI enabled for this user.
          |    wiz3r_ai_enabled INTEGER NOT NULL DEFAULT 0,
          |    -- IANA timezone (e.g. "America/Toronto"). Drives "today" and "current month"
          |    -- boundaries for the user's advice cards. Default UTC = pre-header servers.
          |    timezone TEXT NOT NULL DEFAULT 'UTC'
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS institutions (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    institution_id TEXT UNIQUE NOT NULL,
          |    name TEXT NOT NULL,
          |    country_codes TEXT, -- JSON array of country codes
          |    products TEXT, -- JSON array of supported products
          |    routing_numbers TEXT, -- JSON array of routing numbers
          |    logo_url TEXT,
          |    primary_color TEXT,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS pending_plaid_removals (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    item_id TEXT UNIQUE NOT NULL,
          |    access_token TEXT NOT NULL, -- Encrypted
          |    user_id TEXT, -- For reference only (user may be deleted)
          |    institution_name TEXT,
          |    error_message TEXT,
          |    attempts INTEGER NOT NULL DEFAULT 1,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_attempt_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS plaid_tokens (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    link_token TEXT,
          |    link_token_expiration TEXT,
          |    public_token TEXT,
          |    access_token TEXT NOT NULL, -- Encrypted in production
          |    item_id TEXT UNIQUE NOT NULL,
          |    institution_id TEXT,
          |    institution_name TEXT,
          |    state TEXT NOT NULL DEFAULT 'CREATED',
          |    security_level TEXT NOT NULL DEFAULT 'ACCESS',
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_used_at TEXT,
          |    error_code TEXT,
          |    error_message TEXT,
          |    webhook_url TEXT,
          |    metadata TEXT, -- JSON metadata
          |
          |    -- Plaid item removal tracking
          |    plaid_removal_status TEXT, -- NULL = no removal needed, 'PENDING' = removal failed, 'REMOVED' = confirmed removed
          |
          |    -- Transaction sync management
          |    transactions_cursor TEXT, -- Cursor for /transactions/sync pagination
          |    initial_sync_complete INTEGER NOT NULL DEFAULT 0, -- Boolean: initial sync done
          |    historical_sync_complete INTEGER NOT NULL DEFAULT 0, -- Boolean: full history synced
          |    last_sync_at TEXT, -- Timestamp of last successful sync (auto + user-driven)
          |    last_relinked_at TEXT, -- Timestamp of last user-driven Plaid Link update flow.
          |                           -- Drives the badge UX: shows "Bank isn't returning new
          |                           -- transactions..." when stale + within 48h of relink.
          |    last_transactions_refresh_at TEXT, -- Timestamp of last billed /transactions/refresh
          |                           -- call (Plaid charges ~${'$'}0.30/call). Independent of
          |                           -- last_relinked_at so the user can't reset the rate
          |                           -- limit by spamming Reconnect.
          |    days_requested INTEGER DEFAULT 90, -- Historical data period (90, 180, 365, 730)
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (institution_id) REFERENCES institutions(institution_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS accounts (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    account_id TEXT UNIQUE NOT NULL,
          |    user_id TEXT NOT NULL,
          |    item_id TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    official_name TEXT,
          |    mask TEXT,
          |    type TEXT NOT NULL, -- checking, savings, credit, loan, investment
          |    subtype TEXT,
          |
          |    -- Balance information
          |    balance_available REAL,
          |    balance_current REAL,
          |    balance_limit REAL,
          |    iso_currency_code TEXT,
          |    unofficial_currency_code TEXT,
          |    balance_last_updated TEXT,
          |
          |    -- Account verification
          |    verification_status TEXT,
          |    persistent_account_id TEXT,
          |
          |    -- Timestamps
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_synced_at TEXT,
          |
          |    -- 'active' (default) or 'unlinked'. Soft-delete on user-driven unlink so
          |    -- transaction history (incl. user categorisations, splits, distributions,
          |    -- goal assignments, notes) is preserved for future re-link + merge.
          |    -- Phase A: column + filter only. Phase B will add the merge UI.
          |    status TEXT NOT NULL DEFAULT 'active',
          |
          |    -- User-chosen name for this account. NULL means "use the bank's name".
          |    -- Never written by any sync path: nicknames must survive a refresh.
          |    custom_name TEXT,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (item_id) REFERENCES plaid_tokens(item_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS transactions (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    
          |    -- Core Plaid fields
          |    transaction_id TEXT UNIQUE NOT NULL,
          |    account_id TEXT NOT NULL,
          |    user_id TEXT NOT NULL, -- Denormalized for query efficiency
          |    
          |    -- Financial data
          |    amount REAL NOT NULL,
          |    iso_currency_code TEXT,
          |    unofficial_currency_code TEXT,
          |    
          |    -- Date information
          |    date TEXT NOT NULL, -- YYYY-MM-DD format
          |    authorized_date TEXT,
          |    authorized_datetime TEXT,
          |    datetime TEXT,
          |    
          |    -- Merchant and description
          |    name TEXT NOT NULL,
          |    merchant_name TEXT,
          |    original_description TEXT,
          |    
          |    -- Categories (stored as JSON for flexibility)
          |    category TEXT, -- JSON array of category strings
          |    category_id TEXT,
          |    personal_finance_category_primary TEXT,
          |    personal_finance_category_detailed TEXT,
          |    personal_finance_category_confidence TEXT,
          |    
          |    -- Transaction metadata
          |    transaction_type TEXT,
          |    transaction_code TEXT,
          |    pending INTEGER NOT NULL DEFAULT 0, -- Boolean as integer
          |    pending_transaction_id TEXT,
          |    
          |    -- Location data (JSON)
          |    location_json TEXT, -- Full location object as JSON
          |    
          |    -- Payment metadata (JSON)
          |    payment_meta_json TEXT, -- Full payment metadata as JSON
          |    
          |    -- Account owner
          |    account_owner TEXT,
          |    
          |    -- Local metadata
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_synced_at TEXT,
          |    
          |    -- User customizations
          |    user_category TEXT, -- User-defined category override
          |    
          |    -- ML vector embeddings for similarity search
          |    embedding BLOB, -- Vector embedding for ML categorization
          |    embedding_version INTEGER DEFAULT 1, -- Track embedding model version
          |    user_notes TEXT,
          |    is_hidden INTEGER NOT NULL DEFAULT 0,
          |    is_expense_report INTEGER NOT NULL DEFAULT 0,
          |    tags TEXT, -- JSON array of user-defined tags
          |    
          |    -- AI Categorization (added for budget management feature)
          |    ai_category TEXT, -- AI-generated category prediction
          |    confidence_score REAL, -- ML model confidence (0-100%)
          |    original_ai_category TEXT, -- Original AI prediction before user correction
          |    corrected_at TEXT, -- Timestamp when user corrected AI prediction
          |    correction_confidence REAL, -- Confidence score of original prediction that was corrected
          |    ml_model_version TEXT, -- ML model version that generated prediction
          |    category_source TEXT DEFAULT 'plaid', -- Source: 'plaid', 'ai', 'user', 'pattern_match'
          |
          |    -- Split/Distribute Transaction Support
          |    parent_transaction_id TEXT, -- Links child transaction to parent
          |    child_type TEXT, -- "SPLIT" or "DISTRIBUTED"
          |    is_split_parent INTEGER NOT NULL DEFAULT 0, -- 1 if this transaction has been split
          |    is_distributed_parent INTEGER NOT NULL DEFAULT 0, -- 1 if distributed across months
          |    split_order INTEGER, -- Order of split item (for display)
          |    distribution_start_month TEXT, -- "2025-01" format for distributed transactions
          |    distribution_end_month TEXT, -- "2025-06" format for distributed transactions
          |    distribution_month_index INTEGER, -- 1-based: which month this child represents (1, 2, 3...)
          |    distribution_total_months INTEGER, -- total months in distribution (N in "n/N")
          |
          |    -- User Category Verification
          |    is_verified INTEGER NOT NULL DEFAULT 0, -- 1 if user verified the category
          |    verified_at TEXT, -- Timestamp when user verified
          |
          |    -- Excluded from calculations flag (replaces former "internal transfer" flag)
          |    is_excluded INTEGER NOT NULL DEFAULT 0,
          |
          |    -- Notification tracking
          |    notified_at TEXT,
          |
          |    FOREIGN KEY (account_id) REFERENCES accounts(account_id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (parent_transaction_id) REFERENCES transactions(transaction_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS transaction_categories (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    parent_id INTEGER, -- For hierarchical categories
          |    color TEXT,
          |    icon TEXT,
          |    is_system INTEGER NOT NULL DEFAULT 0, -- System vs user-defined
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (parent_id) REFERENCES transaction_categories(id) ON DELETE CASCADE,
          |    UNIQUE(user_id, name)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS user_corrections (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    transaction_id TEXT NOT NULL, -- Transaction that was corrected
          |    user_id TEXT NOT NULL, -- User who made the correction
          |    old_category TEXT NOT NULL, -- AI's original prediction
          |    new_category TEXT NOT NULL, -- User's corrected category
          |    original_confidence REAL, -- Confidence of original AI prediction
          |    correction_reason TEXT, -- Optional: why user corrected (future use)
          |    timestamp TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    
          |    -- ML learning metadata
          |    ml_model_version TEXT, -- Version of model that made original prediction
          |    merchant_name TEXT, -- Denormalized for pattern learning
          |    transaction_amount REAL, -- Denormalized for amount-based learning
          |    
          |    FOREIGN KEY (transaction_id) REFERENCES transactions(transaction_id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS transaction_embeddings (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    transaction_id TEXT UNIQUE NOT NULL, -- Link to transaction
          |    user_id TEXT NOT NULL, -- For user-specific embeddings
          |    
          |    -- Vector storage (sqlite-vec integration)
          |    embedding_vector BLOB, -- Vector embedding as binary data
          |    embedding_dimension INTEGER NOT NULL, -- Dimension size (typically 384 or 768)
          |    
          |    -- Metadata for embedding generation
          |    source_text TEXT NOT NULL, -- Original text that was embedded (merchant + description)
          |    embedding_model TEXT NOT NULL, -- Model used to generate embedding
          |    embedding_version TEXT, -- Version of embedding model
          |    
          |    -- Performance and categorization
          |    category_hint TEXT, -- Category associated with this embedding
          |    similarity_threshold REAL DEFAULT 0.7, -- Threshold for similarity matching
          |    
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    
          |    FOREIGN KEY (transaction_id) REFERENCES transactions(transaction_id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS savings_goals (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    user_id TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    description TEXT DEFAULT '',
          |    target_amount REAL NOT NULL,
          |    current_amount REAL DEFAULT 0.0,
          |    deadline TEXT NOT NULL, -- ISO date format YYYY-MM-DD
          |    category TEXT NOT NULL, -- GoalCategory enum as string
          |    priority TEXT DEFAULT 'MEDIUM', -- GoalPriority enum as string
          |    created_date TEXT NOT NULL, -- ISO instant format
          |    is_active INTEGER NOT NULL DEFAULT 1,
          |    icon_emoji TEXT DEFAULT '💰',
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_modified TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP, -- For backward compatibility
          |    -- Wiz 3R AI fields. Null until the user sets them via the goal edit UI.
          |    -- Drive whether the AI applies precise (set) or conditional (null) frameworks.
          |    account_type TEXT,                  -- GoalAccountType enum as string, e.g. "RRSP", "TFSA"
          |    debt_interest_rate REAL,            -- Only meaningful for DEBT_PAYOFF goals
          |    debt_remaining_balance REAL,        -- Only meaningful for DEBT_PAYOFF goals
          |    debt_remaining_months INTEGER,      -- Only meaningful for DEBT_PAYOFF goals
          |    debt_payment_frequency TEXT,             -- NEW — PaymentFrequency enum name
          |    debt_monthly_equivalent_payment REAL,    -- NEW — monthly-equivalent payment
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS savings_recommendations (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    goal_id TEXT NOT NULL,
          |    user_id TEXT NOT NULL,
          |    spending_category TEXT NOT NULL,
          |    current_monthly_spending REAL NOT NULL,
          |    recommended_monthly_spending REAL NOT NULL,
          |    suggested_reduction REAL NOT NULL,
          |    rationale TEXT NOT NULL,
          |    potential_savings REAL NOT NULL,
          |    confidence_score REAL NOT NULL,
          |    priority INTEGER NOT NULL,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    is_applied INTEGER NOT NULL DEFAULT 0,
          |    applied_at TEXT,
          |
          |    FOREIGN KEY (goal_id) REFERENCES savings_goals(id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS goal_progress_history (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    goal_id TEXT NOT NULL,
          |    user_id TEXT NOT NULL,
          |    contribution_amount REAL NOT NULL,
          |    contribution_date TEXT NOT NULL,
          |    notes TEXT,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (goal_id) REFERENCES savings_goals(id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS goal_assignment_rules (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    goal_id TEXT NOT NULL,
          |    merchant_name TEXT,           -- Normalized merchant name for matching
          |    account_id TEXT,              -- Specific account (for internal transfers)
          |    description_pattern TEXT,     -- Optional regex pattern for description
          |    priority INTEGER DEFAULT 0,   -- Higher priority wins in conflicts
          |    is_default INTEGER DEFAULT 0, -- Is this the default rule for this pattern?
          |    rule_mode TEXT DEFAULT 'always_assign', -- 'always_assign', 'always_ask', 'default_goal'
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (goal_id) REFERENCES savings_goals(id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS transaction_goal_assignments (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    transaction_id TEXT NOT NULL,
          |    goal_id TEXT NOT NULL,
          |    user_id TEXT NOT NULL,
          |    amount_allocated REAL NOT NULL,  -- Amount from transaction applied to goal
          |    assignment_source TEXT DEFAULT 'user', -- 'user', 'auto', 'rule'
          |    rule_id INTEGER,                  -- Reference to the rule that auto-assigned
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (transaction_id) REFERENCES transactions(transaction_id) ON DELETE CASCADE,
          |    FOREIGN KEY (goal_id) REFERENCES savings_goals(id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (rule_id) REFERENCES goal_assignment_rules(id) ON DELETE SET NULL,
          |    UNIQUE(transaction_id, goal_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS contexts (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    user_id TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    color TEXT NOT NULL,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS transaction_contexts (
          |    transaction_id TEXT NOT NULL,
          |    context_id TEXT NOT NULL,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    PRIMARY KEY (transaction_id, context_id),
          |    FOREIGN KEY (context_id) REFERENCES contexts(id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS budget_plans (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    user_id TEXT NOT NULL,
          |    year INTEGER NOT NULL,
          |    month INTEGER NOT NULL,  -- 1-12
          |    category_type TEXT NOT NULL,  -- 'income' or 'expense'
          |    category_name TEXT NOT NULL,
          |    planned_amount REAL NOT NULL DEFAULT 0,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    UNIQUE(user_id, year, month, category_type, category_name)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS advice_cards (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    category TEXT NOT NULL,  -- 'savings_tips', 'goal_setting', 'accounts', 'encouragement', 'behavioral'
          |    title TEXT NOT NULL,
          |    content TEXT NOT NULL,
          |    icon TEXT,
          |    priority INTEGER DEFAULT 0,
          |    is_active INTEGER DEFAULT 1,
          |    country TEXT  -- NULL = universal, 'CA' = Canada-only, 'US' = US-only
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS user_advice_cards (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    user_id TEXT NOT NULL,
          |    card_id TEXT NOT NULL,
          |    is_pinned INTEGER DEFAULT 0,
          |    is_dismissed INTEGER DEFAULT 0,
          |    viewed_at TEXT,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (card_id) REFERENCES advice_cards(id) ON DELETE CASCADE,
          |    UNIQUE(user_id, card_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS user_settings (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    setting_key TEXT NOT NULL,
          |    setting_value TEXT NOT NULL,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    UNIQUE(user_id, setting_key)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS dismissed_personalized_cards (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    card_id TEXT NOT NULL,
          |    dismissed_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    expires_at TEXT,  -- Optional: card can reappear after this date
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    UNIQUE(user_id, card_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS personalized_card_views (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    card_id TEXT NOT NULL,
          |    shown_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    shown_count INTEGER NOT NULL DEFAULT 1,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    UNIQUE(user_id, card_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS user_custom_categories (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    primary_name TEXT NOT NULL,
          |    secondary_name TEXT NOT NULL,
          |    is_income INTEGER NOT NULL DEFAULT 0,
          |    icon TEXT,
          |    created_at TEXT NOT NULL DEFAULT (datetime('now')),
          |    -- Soft-delete: NULL while active, set to a timestamp when the user
          |    -- removes the category. We keep the row around so transactions that
          |    -- still reference it (by name in personal_finance_category_*) can
          |    -- continue to resolve their display label, even though the category
          |    -- no longer appears in pickers. Hard-deleting used to leave dangling
          |    -- references whenever the AI re-categorize-on-delete path failed.
          |    deleted_at TEXT
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS subscription_merchants (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    merchant_key TEXT NOT NULL,       -- Normalized merchant name (lowercase)
          |    typical_amount REAL,              -- Typical recurring amount
          |    status TEXT NOT NULL DEFAULT 'detected', -- 'detected', 'confirmed', 'dismissed'
          |    confirmed_at TEXT,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    UNIQUE(user_id, merchant_key)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS statement_templates (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    institution_signature TEXT NOT NULL,
          |    institution_alias TEXT,
          |    file_type TEXT NOT NULL DEFAULT 'csv',
          |    column_mapping TEXT,
          |    date_format TEXT,
          |    delimiter TEXT,
          |    template_data TEXT,
          |    is_valid INTEGER NOT NULL DEFAULT 1,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS manual_accounts (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    account_id TEXT UNIQUE NOT NULL,
          |    user_id TEXT NOT NULL,
          |    institution_name TEXT NOT NULL,
          |    account_name TEXT NOT NULL,
          |    account_type TEXT NOT NULL DEFAULT 'checking',
          |    balance REAL NOT NULL DEFAULT 0.0,
          |    template_id INTEGER,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE,
          |    FOREIGN KEY (template_id) REFERENCES statement_templates(id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS notification_tokens (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    token TEXT UNIQUE NOT NULL,
          |    transaction_ids TEXT NOT NULL,
          |    phone_number TEXT,
          |    action TEXT,
          |    created_at TEXT NOT NULL,
          |    expires_at TEXT NOT NULL,
          |    acted_at TEXT,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS wiz3r_card_generations (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    user_id TEXT NOT NULL,
          |    card_id TEXT NOT NULL,
          |    generated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    context_json TEXT NOT NULL,
          |    response_json TEXT NOT NULL,
          |
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS wiz3r_chat_threads (
          |    thread_id TEXT PRIMARY KEY,
          |    user_id TEXT NOT NULL,
          |    card_id TEXT NOT NULL,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    last_message_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    UNIQUE(user_id, card_id),
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS wiz3r_chat_messages (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    thread_id TEXT NOT NULL,
          |    user_id TEXT NOT NULL,         -- denormalized for rate-limit queries
          |    role TEXT NOT NULL,            -- 'user' | 'assistant'
          |    content TEXT NOT NULL,
          |    chart_json TEXT,               -- serialized ChartSpec when the assistant invoked show_chart
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |
          |    FOREIGN KEY (thread_id) REFERENCES wiz3r_chat_threads(thread_id) ON DELETE CASCADE,
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS budget_category_classifications (
          |    user_id TEXT NOT NULL,
          |    category_code TEXT NOT NULL,
          |    bucket TEXT NOT NULL,
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    updated_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    PRIMARY KEY (user_id, category_code),
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS budget_health_snapshots (
          |    user_id TEXT NOT NULL,
          |    year_month TEXT NOT NULL,         -- "YYYY-MM"
          |    needs_pct REAL NOT NULL,
          |    wants_pct REAL NOT NULL,
          |    savings_pct REAL NOT NULL,
          |    health_label TEXT NOT NULL,       -- BALANCED | OVER_INDEXED_NEEDS | UNDER_SAVING | OVER_COMMITTED
          |    created_at TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP,
          |    PRIMARY KEY (user_id, year_month),
          |    FOREIGN KEY (user_id) REFERENCES users(user_id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_accounts_user_id ON accounts(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_user_id ON transactions(user_id)", 0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_plaid_tokens_user_id ON plaid_tokens(user_id)", 0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_account_id ON transactions(account_id)",
          0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_accounts_item_id ON accounts(item_id)",
          0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_transactions_date ON transactions(date)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_user_date ON transactions(user_id, date)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_account_date ON transactions(account_id, date)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_category ON transactions(personal_finance_category_primary)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_user_category ON transactions(user_category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_ai_category ON transactions(ai_category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_category_source ON transactions(category_source)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_confidence ON transactions(confidence_score)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_corrections_transaction_id ON user_corrections(transaction_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_corrections_user_id ON user_corrections(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_corrections_timestamp ON user_corrections(timestamp)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_corrections_old_category ON user_corrections(old_category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_corrections_new_category ON user_corrections(new_category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_corrections_merchant ON user_corrections(merchant_name)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_embeddings_transaction_id ON transaction_embeddings(transaction_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_embeddings_user_id ON transaction_embeddings(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_embeddings_model ON transaction_embeddings(embedding_model)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_embeddings_category ON transaction_embeddings(category_hint)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_pending ON transactions(pending)", 0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_accounts_type ON accounts(type)",
          0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_transactions_name ON transactions(name)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_merchant ON transactions(merchant_name)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_parent_id ON transactions(parent_transaction_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_child_type ON transactions(child_type)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_split_parent ON transactions(is_split_parent)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transactions_distributed_parent ON transactions(is_distributed_parent)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_goal_rules_user_merchant ON goal_assignment_rules(user_id, merchant_name)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_goal_rules_user_account ON goal_assignment_rules(user_id, account_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_goal_rules_goal ON goal_assignment_rules(goal_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_goal_assignments_transaction ON transaction_goal_assignments(transaction_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_goal_assignments_goal ON transaction_goal_assignments(goal_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_goal_assignments_user ON transaction_goal_assignments(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_savings_goals_user_id ON savings_goals(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_savings_goals_deadline ON savings_goals(deadline)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_savings_goals_category ON savings_goals(category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_savings_goals_active ON savings_goals(is_active)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_recommendations_goal_id ON savings_recommendations(goal_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_recommendations_user_id ON savings_recommendations(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_recommendations_category ON savings_recommendations(spending_category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_progress_history_goal_id ON goal_progress_history(goal_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_progress_history_date ON goal_progress_history(contribution_date)",
          0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_contexts_user_id ON contexts(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transaction_contexts_context_id ON transaction_contexts(context_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_transaction_contexts_transaction_id ON transaction_contexts(transaction_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_budget_plans_user_id ON budget_plans(user_id)", 0).await()
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_budget_plans_year ON budget_plans(year)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_budget_plans_year_month ON budget_plans(user_id, year, month)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_budget_plans_category_type ON budget_plans(category_type)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_advice_cards_category ON advice_cards(category)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_advice_cards_active ON advice_cards(is_active)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_user_advice_cards_user_id ON user_advice_cards(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_user_advice_cards_card_id ON user_advice_cards(card_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_user_advice_cards_pinned ON user_advice_cards(is_pinned)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_user_settings_user_id ON user_settings(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_user_settings_key ON user_settings(user_id, setting_key)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_dismissed_personalized_user ON dismissed_personalized_cards(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_dismissed_personalized_card ON dismissed_personalized_cards(user_id, card_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_personalized_views_user ON personalized_card_views(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_notification_tokens_token ON notification_tokens(token)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_notification_tokens_phone ON notification_tokens(phone_number)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_card_gen_user ON wiz3r_card_generations(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_card_gen_user_card ON wiz3r_card_generations(user_id, card_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_card_gen_generated_at ON wiz3r_card_generations(generated_at)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_chat_threads_user ON wiz3r_chat_threads(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_chat_threads_user_card ON wiz3r_chat_threads(user_id, card_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_chat_msgs_thread ON wiz3r_chat_messages(thread_id, created_at)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_wiz3r_chat_msgs_user_created ON wiz3r_chat_messages(user_id, created_at)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_budget_cat_class_user ON budget_category_classifications(user_id)",
          0).await()
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_budget_health_user_month ON budget_health_snapshots(user_id, year_month)",
          0).await()
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
    }
  }
}
